import { computed, signal } from '@angular/core';
import { CustomRule, CustomRuleGroup } from '../../models/filter-models';
import {
  customRuleGroupsProcessingOrder,
  moveCustomRuleBefore,
  appendCustomRuleToGroup,
  moveCustomRuleToFirstInGroup,
  moveNonDefaultGroupBefore,
  moveNonDefaultGroupToEnd,
  moveNonDefaultGroupFirstAmongExtras,
} from './custom-rule-groups-helper';

export type ReorderDropHighlight =
  | { t: 'rule'; rule: CustomRule; group: CustomRuleGroup }
  | { t: 'ruleEnd'; group: CustomRuleGroup }
  | { t: 'ruleHeader'; group: CustomRuleGroup }
  | { t: 'groupHeader'; group: CustomRuleGroup }
  | { t: 'groupEnd' };

export class CustomRuleGroupsReorderController {
  readonly reorder = signal(false);
  readonly draggedRule = signal<CustomRule | null>(null);
  readonly draggedRuleSource = signal<CustomRuleGroup | null>(null);
  readonly draggedGroup = signal<CustomRuleGroup | null>(null);
  readonly dropHighlight = signal<ReorderDropHighlight | null>(null);

  private readonly groupsRevision = signal(0);

  readonly groupsInOrder = computed(() => {
    this.groupsRevision();
    return customRuleGroupsProcessingOrder(this.getGroups());
  });

  readonly groupCount = computed(() => {
    this.groupsRevision();
    return this.getGroups().length;
  });

  private readonly notifyUpdate: () => void;

  constructor(
    private readonly getGroups: () => CustomRuleGroup[],
    onUpdate: () => void,
  ) {
    this.notifyUpdate = () => {
      this.invalidateGroups();
      onUpdate();
    };
  }

  invalidateGroups(): void {
    this.groupsRevision.update(v => v + 1);
  }

  setReorder(value: boolean) {
    this.reorder.set(value);
    if (!value) {
      this.clearDragState();
    }
  }

  onRuleDragStart(payload: { rule: CustomRule; group: CustomRuleGroup }) {
    this.draggedRule.set(payload.rule);
    this.draggedRuleSource.set(payload.group);
    this.draggedGroup.set(null);
    this.dropHighlight.set(null);
  }

  onRuleDragEnd() {
    this.finishDrag();
  }

  onRuleDragEnterTarget(payload: { rule: CustomRule; group: CustomRuleGroup }) {
    if (!this.reorder() || !this.draggedRule() || !this.draggedRuleSource()) return;
    if (payload.rule === this.draggedRule()) return;
    this.setDropHighlight({ t: 'rule', rule: payload.rule, group: payload.group });
  }

  onRuleDragOverTarget(payload: { ev: DragEvent; rule: CustomRule; group: CustomRuleGroup }) {
    if (!this.reorder() || !this.draggedRule() || !this.draggedRuleSource()) return;
    if (payload.rule === this.draggedRule()) return;

    const rules = payload.group.rules;
    const lastRule = rules[rules.length - 1];
    if (lastRule && payload.rule === lastRule) {
      const row = payload.ev.currentTarget as HTMLElement;
      const mid = row.getBoundingClientRect().top + row.offsetHeight / 2;
      if (payload.ev.clientY >= mid) {
        this.setDropHighlight({ t: 'ruleEnd', group: payload.group });
        return;
      }
    }

    this.setDropHighlight({ t: 'rule', rule: payload.rule, group: payload.group });
  }

  onRuleDragEnterEnd(group: CustomRuleGroup) {
    if (!this.reorder() || !this.draggedRule() || !this.draggedRuleSource()) return;
    this.setDropHighlight({ t: 'ruleEnd', group });
  }

  onGroupDragStart(group: CustomRuleGroup) {
    this.draggedGroup.set(group);
    this.draggedRule.set(null);
    this.draggedRuleSource.set(null);
    this.dropHighlight.set(null);
  }

  onGroupDragEnd() {
    this.finishDrag();
  }

  onGroupDragEnterHeader(targetGroup: CustomRuleGroup) {
    if (!this.reorder()) return;
    if (this.draggedRule() && this.draggedRuleSource()) {
      this.setDropHighlight({ t: 'ruleHeader', group: targetGroup });
      return;
    }
    if (!this.draggedGroup() || this.draggedGroup()!.isDefault) return;
    if (this.draggedGroup() === targetGroup) return;
    this.setDropHighlight({ t: 'groupHeader', group: targetGroup });
  }

  onGroupDragOverHeader(payload: { ev: DragEvent; targetGroup: CustomRuleGroup }) {
    if (!this.reorder()) return;
    if (this.draggedRule() && this.draggedRuleSource()) {
      this.setDropHighlight({ t: 'ruleHeader', group: payload.targetGroup });
      return;
    }
    if (!this.draggedGroup() || this.draggedGroup()!.isDefault) return;
    if (this.draggedGroup() === payload.targetGroup) return;

    const extras = customRuleGroupsProcessingOrder(this.getGroups()).filter(g => !g.isDefault);
    const lastExtra = extras[extras.length - 1];
    if (lastExtra && payload.targetGroup === lastExtra) {
      const header = payload.ev.currentTarget as HTMLElement;
      const mid = header.getBoundingClientRect().top + header.offsetHeight / 2;
      if (payload.ev.clientY >= mid) {
        this.setDropHighlight({ t: 'groupEnd' });
        return;
      }
    }

    this.setDropHighlight({ t: 'groupHeader', group: payload.targetGroup });
  }

  onGroupDragEnterEnd(ev: DragEvent) {
    if (!this.reorder() || !this.draggedGroup() || this.draggedGroup()!.isDefault) return;
    ev.preventDefault();
    this.setDropHighlight({ t: 'groupEnd' });
  }

  onGroupEndStripDragOver(ev: DragEvent) {
    if (this.reorder() && this.draggedGroup() && !this.draggedGroup()!.isDefault) {
      ev.preventDefault();
    }
  }

  onGroupEndStripDrop(ev: DragEvent): void {
    ev.preventDefault();
    this.commitDrop();
  }

  commitDrop(): void {
    if (!this.reorder() || !this.dropHighlight()) return;

    const before = this.captureOrderSnapshot();
    this.applyDropHighlight();
    if (!this.sameOrderSnapshot(before, this.captureOrderSnapshot())) {
      this.notifyUpdate();
    }
    this.clearDragState();
  }

  // Commit last highlighted position when release is not over a drop target (e.g. Add Rule button).
  private finishDrag(): void {
    if (this.reorder() && this.dropHighlight()) {
      this.commitDrop();
      return;
    }
    this.clearDragState();
  }

  private clearDragState(): void {
    this.draggedRule.set(null);
    this.draggedRuleSource.set(null);
    this.draggedGroup.set(null);
    this.dropHighlight.set(null);
  }

  private setDropHighlight(highlight: ReorderDropHighlight): void {
    if (sameHighlight(this.dropHighlight(), highlight)) return;
    this.dropHighlight.set(highlight);
  }

  private applyDropHighlight(): void {
    const h = this.dropHighlight();
    if (!h) return;

    const draggedRule = this.draggedRule();
    const draggedRuleSource = this.draggedRuleSource();
    const draggedGroup = this.draggedGroup();

    switch (h.t) {
      case 'rule':
        if (draggedRule && draggedRuleSource) {
          moveCustomRuleBefore(draggedRule, draggedRuleSource, h.rule, h.group);
        }
        break;
      case 'ruleEnd':
        if (draggedRule && draggedRuleSource) {
          appendCustomRuleToGroup(draggedRule, draggedRuleSource, h.group);
        }
        break;
      case 'ruleHeader':
        if (draggedRule && draggedRuleSource) {
          moveCustomRuleToFirstInGroup(draggedRule, draggedRuleSource, h.group);
        }
        break;
      case 'groupHeader':
        if (draggedGroup && !draggedGroup.isDefault) {
          if (h.group.isDefault) {
            moveNonDefaultGroupFirstAmongExtras(draggedGroup, this.getGroups());
          } else {
            moveNonDefaultGroupBefore(draggedGroup, h.group, this.getGroups());
          }
        }
        break;
      case 'groupEnd':
        if (draggedGroup && !draggedGroup.isDefault) {
          moveNonDefaultGroupToEnd(draggedGroup, this.getGroups());
        }
        break;
    }
  }

  private captureOrderSnapshot(): { group: CustomRuleGroup; rules: CustomRule[] }[] {
    return customRuleGroupsProcessingOrder(this.getGroups()).map(g => ({
      group: g,
      rules: [...g.rules],
    }));
  }

  private sameOrderSnapshot(
    a: { group: CustomRuleGroup; rules: CustomRule[] }[],
    b: { group: CustomRuleGroup; rules: CustomRule[] }[],
  ): boolean {
    if (a.length !== b.length) return false;
    return a.every((entry, i) => {
      const other = b[i]!;
      if (entry.group.order !== other.group.order) return false;
      if (entry.rules.length !== other.rules.length) return false;
      return entry.rules.every((r, ri) => r === other.rules[ri]);
    });
  }
}

function sameHighlight(a: ReorderDropHighlight | null, b: ReorderDropHighlight | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.t !== b.t) return false;
  switch (a.t) {
    case 'rule':
      return b.t === 'rule' && a.rule === b.rule && a.group === b.group;
    case 'ruleEnd':
    case 'ruleHeader':
    case 'groupHeader':
      return b.t === a.t && a.group === b.group;
    case 'groupEnd':
      return b.t === 'groupEnd';
    default:
      return false;
  }
}
